import { allIsraelSettlements, type IsraelSettlement } from '../utils/israelSettlements';
import { isKnownIsraelStreet } from '../utils/israelStreets';

export type VoiceSearchCityMatch = {
  /** Hebrew locality name (what addresses usually store). */
  cityHe: string;
  cityEn: string;
  matchedPhrase: string;
};

const HEBREW_PREFIXES = new Set(['ב', 'ל', 'מ', 'ו']);

const SPOKEN_ALIASES: Array<{ phrase: string; he: string }> = [
  { phrase: 'tel aviv', he: 'תל אביב - יפו' },
  { phrase: 'tel-aviv', he: 'תל אביב - יפו' },
  { phrase: 'telaviv', he: 'תל אביב - יפו' },
  { phrase: 'תל אביב', he: 'תל אביב - יפו' },
  { phrase: 'תלאביב', he: 'תל אביב - יפו' },
  { phrase: 'jerusalem', he: 'ירושלים' },
  { phrase: 'ירושלים', he: 'ירושלים' },
  { phrase: 'haifa', he: 'חיפה' },
  { phrase: 'חיפה', he: 'חיפה' },
  { phrase: 'herzliya', he: 'הרצליה' },
  { phrase: 'herzelia', he: 'הרצליה' },
  { phrase: 'הרצליה', he: 'הרצליה' },
  { phrase: 'ramat gan', he: 'רמת גן' },
  { phrase: 'רמת גן', he: 'רמת גן' },
  { phrase: 'beer sheva', he: 'באר שבע' },
  { phrase: 'beersheba', he: 'באר שבע' },
  { phrase: 'באר שבע', he: 'באר שבע' },
  { phrase: 'netanya', he: 'נתניה' },
  { phrase: 'נתניה', he: 'נתניה' },
  { phrase: 'rishon lezion', he: 'ראשון לציון' },
  { phrase: 'rishon leziyon', he: 'ראשון לציון' },
  { phrase: 'ראשון לציון', he: 'ראשון לציון' },
  { phrase: 'petah tikva', he: 'פתח תקווה' },
  { phrase: 'petach tikva', he: 'פתח תקווה' },
  { phrase: 'פתח תקווה', he: 'פתח תקווה' },
  { phrase: 'פתח תקוה', he: 'פתח תקווה' },
  { phrase: 'ashdod', he: 'אשדוד' },
  { phrase: 'אשדוד', he: 'אשדוד' },
  { phrase: 'holon', he: 'חולון' },
  { phrase: 'חולון', he: 'חולון' },
  { phrase: 'eilat', he: 'אילת' },
  { phrase: 'אילת', he: 'אילת' },
  { phrase: 'rehovot', he: 'רחובות' },
  { phrase: 'רחובות', he: 'רחובות' },
  { phrase: 'kfar saba', he: 'כפר סבא' },
  { phrase: 'כפר סבא', he: 'כפר סבא' },
  { phrase: 'raanana', he: 'רעננה' },
  { phrase: "ra'anana", he: 'רעננה' },
  { phrase: 'רעננה', he: 'רעננה' },
  { phrase: 'bat yam', he: 'בת ים' },
  { phrase: 'בת ים', he: 'בת ים' },
  { phrase: 'kiryat ono', he: 'קרית אונו' },
  { phrase: 'qiryat ono', he: 'קרית אונו' },
  { phrase: 'קריית אונו', he: 'קרית אונו' },
];

type CityPhrase = {
  phrase: string;
  settlement: IsraelSettlement;
};

let phraseIndex: CityPhrase[] | null = null;
let settlementByHe: Map<string, IsraelSettlement> | null = null;

/** CBS uses קרית; people often type קריית. Fold so both match. */
export function foldHebrewKiryatSpelling(value: string): string {
  return value.replace(/קריית/g, 'קרית');
}

export function normalizeVoiceText(value: string): string {
  return foldHebrewKiryatSpelling(value)
    .replace(/[׳']/g, "'")
    .replace(/[־–—-]+/g, ' ')
    .replace(/[()]/g, ' ')
    .replace(/[?؟!]+/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function isWordChar(ch: string): boolean {
  return /[a-z0-9\u0590-\u05ff]/i.test(ch);
}

function phraseIndexOf(haystack: string, phrase: string): number {
  if (!phrase || haystack.length < phrase.length) {
    return -1;
  }
  let from = 0;
  while (from <= haystack.length - phrase.length) {
    const idx = haystack.indexOf(phrase, from);
    if (idx < 0) {
      return -1;
    }
    const before = idx === 0 ? '' : haystack[idx - 1];
    const after = haystack[idx + phrase.length] ?? '';
    const beforeOk = !before || !isWordChar(before) || HEBREW_PREFIXES.has(before);
    const afterOk = !after || !isWordChar(after);
    if (beforeOk && afterOk) {
      return idx;
    }
    from = idx + 1;
  }
  return -1;
}

function settlementsByHebrew(): Map<string, IsraelSettlement> {
  if (settlementByHe) {
    return settlementByHe;
  }
  const map = new Map<string, IsraelSettlement>();
  for (const settlement of allIsraelSettlements()) {
    const he = settlement.he.trim();
    if (he) {
      map.set(he, settlement);
    }
  }
  settlementByHe = map;
  return map;
}

function addPhrase(out: CityPhrase[], seen: Set<string>, phrase: string, settlement: IsraelSettlement): void {
  const normalized = normalizeVoiceText(phrase);
  if (normalized.length < 3) {
    return;
  }
  const key = `${normalized}\0${settlement.he}`;
  if (seen.has(key)) {
    return;
  }
  seen.add(key);
  out.push({ phrase: normalized, settlement });
}

function getCityPhraseIndex(): CityPhrase[] {
  if (phraseIndex) {
    return phraseIndex;
  }
  const out: CityPhrase[] = [];
  const seen = new Set<string>();
  const byHe = settlementsByHebrew();

  for (const settlement of allIsraelSettlements()) {
    if (settlement.he.includes('שבט')) {
      continue;
    }
    addPhrase(out, seen, settlement.he, settlement);
    addPhrase(out, seen, settlement.en, settlement);
    const heHead = settlement.he.split(/\s*[-–]\s*/)[0] ?? '';
    const enHead = settlement.en.split(/\s*[-–]\s*/)[0] ?? '';
    addPhrase(out, seen, heHead, settlement);
    addPhrase(out, seen, enHead, settlement);
    // Glued forms: "בתלאביב" still matches Tel Aviv.
    for (const phrase of [settlement.he, settlement.en, heHead, enHead]) {
      const compact = normalizeVoiceText(phrase).replace(/\s+/g, '');
      if (compact.length >= 5 && compact !== normalizeVoiceText(phrase)) {
        addPhrase(out, seen, compact, settlement);
      }
    }
  }

  for (const alias of SPOKEN_ALIASES) {
    const settlement = byHe.get(alias.he);
    if (settlement) {
      addPhrase(out, seen, alias.phrase, settlement);
    }
  }

  out.sort((a, b) => b.phrase.length - a.phrase.length);
  phraseIndex = out;
  return out;
}

const STREET_WORD_CITY_FALSE_POSITIVES = new Set([
  'רחוב',
  'street',
  'st',
  'road',
  'rd',
  'avenue',
  'ave',
]);

function isStreetWordCityFalsePositive(haystack: string, phrase: string): boolean {
  const needle = normalizeVoiceText(phrase);
  if (!STREET_WORD_CITY_FALSE_POSITIVES.has(needle)) {
    return false;
  }
  const idx = phraseIndexOf(haystack, needle);
  if (idx < 0) {
    return false;
  }
  const after = haystack.slice(idx + needle.length).trim();
  return after.length >= 2;
}

function isPhraseSpokenAsStreet(haystack: string, phrase: string): boolean {
  const needle = normalizeVoiceText(phrase);
  const idx = phraseIndexOf(haystack, needle);
  if (idx < 0) {
    return false;
  }
  const before = haystack.slice(0, idx).trim();
  if (/(?:^|\s)(?:ברחוב|רחוב|on|at)$/i.test(before)) {
    return true;
  }
  const after = haystack.slice(idx + needle.length).trim();
  return /^(?:street|st|rd|road|avenue|ave|blvd|boulevard)\b/i.test(after);
}

export type MatchVoiceSearchCityOptions = {
  /** Current search city — do not treat its street names as a new locality. */
  preferStreetInCity?: string | null;
};

/** True when a and b differ by at most one insert/delete/substitute. */
function withinOneEdit(a: string, b: string): boolean {
  if (a === b) {
    return true;
  }
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) {
    return false;
  }
  if (la > lb) {
    return withinOneEdit(b, a);
  }
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < la && j < lb) {
    if (a[i] === b[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) {
      return false;
    }
    if (la === lb) {
      i += 1;
      j += 1;
    } else {
      j += 1;
    }
  }
  if (j < lb || i < la) {
    edits += 1;
  }
  return edits <= 1;
}

/**
 * Recover common speech/typo near-misses for two-word Hebrew cities
 * (“בל אביב” → תל אביב) without a phrase blacklist.
 */
function matchNearMissTwoWordHebrewCity(haystack: string): VoiceSearchCityMatch | null {
  const tokens = haystack.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) {
    return null;
  }
  for (const entry of getCityPhraseIndex()) {
    if (!/[\u0590-\u05ff]/.test(entry.phrase)) {
      continue;
    }
    const phraseTokens = entry.phrase.split(/\s+/).filter(Boolean);
    if (phraseTokens.length !== 2) {
      continue;
    }
    const first = phraseTokens[0] ?? '';
    const second = phraseTokens[1] ?? '';
    if (first.length < 2 || second.length < 3) {
      continue;
    }
    for (let i = 1; i < tokens.length; i += 1) {
      const last = tokens[i] ?? '';
      if (last !== second) {
        continue;
      }
      const prev = tokens[i - 1] ?? '';
      const prevStem =
        prev.length >= 3 && HEBREW_PREFIXES.has(prev[0] ?? '') ? prev.slice(1) : prev;
      if (!withinOneEdit(prev, first) && !withinOneEdit(prevStem, first)) {
        continue;
      }
      if (isPhraseSpokenAsStreet(haystack, `${prev} ${last}`)) {
        continue;
      }
      return {
        cityHe: entry.settlement.he.trim(),
        cityEn: entry.settlement.en.trim(),
        matchedPhrase: `${prev} ${last}`,
      };
    }
  }
  return null;
}

export function matchVoiceSearchCity(
  text: string,
  options?: MatchVoiceSearchCityOptions,
): VoiceSearchCityMatch | null {
  const haystack = normalizeVoiceText(text);
  if (!haystack) {
    return null;
  }
  const preferStreetInCity = options?.preferStreetInCity?.trim() || '';
  for (const entry of getCityPhraseIndex()) {
    if (phraseIndexOf(haystack, entry.phrase) < 0) {
      continue;
    }
    if (isStreetWordCityFalsePositive(haystack, entry.phrase)) {
      continue;
    }
    if (isPhraseSpokenAsStreet(haystack, entry.phrase)) {
      continue;
    }
    const cityHe = entry.settlement.he.trim();
    if (
      preferStreetInCity &&
      preferStreetInCity !== cityHe &&
      (isKnownIsraelStreet(preferStreetInCity, cityHe) ||
        isKnownIsraelStreet(preferStreetInCity, entry.phrase.trim()))
    ) {
      continue;
    }
    return {
      cityHe,
      cityEn: entry.settlement.en.trim(),
      matchedPhrase: entry.phrase,
    };
  }
  return matchNearMissTwoWordHebrewCity(haystack);
}

export function removeVoicePhrase(text: string, phrase: string): string {
  const haystack = normalizeVoiceText(text);
  const needle = normalizeVoiceText(phrase);
  const idx = phraseIndexOf(haystack, needle);
  if (idx < 0) {
    return haystack;
  }
  let start = idx;
  if (start > 0 && HEBREW_PREFIXES.has(haystack[start - 1] ?? '')) {
    const beforePrefix = start >= 2 ? haystack[start - 2] : '';
    if (!beforePrefix || !isWordChar(beforePrefix)) {
      start -= 1;
    }
  }
  return `${haystack.slice(0, start)} ${haystack.slice(idx + needle.length)}`.replace(/\s+/g, ' ').trim();
}

export function stripHebrewPrefixLetters(text: string): string {
  return normalizeVoiceText(text)
    .split(' ')
    .filter((token) => token.length > 1 || !HEBREW_PREFIXES.has(token))
    .join(' ')
    .trim();
}
